import { useCurrencyArithmetic } from '@shared/composables/useCurrencyArithmetic';
import type { Cartv2PackageDisplayModel } from '@shared/models/carts/cartPackages';
import { PackageAddonTypes } from '@shared/models/packages/packageAddons';
import type { BasePackageDisplayModel } from '@shared/models/packages/packages';
import { PackageEditModel } from '@shared/models/packages/packageSetsV3';

type PackageItems<T> = BasePackageDisplayModel<T>[] | Cartv2PackageDisplayModel[] | undefined;
const { subtract } = useCurrencyArithmetic();

export const usePackageAddonService = () => {
  const isStandardPackage = <T>(pkg: BasePackageDisplayModel<T> | Cartv2PackageDisplayModel | PackageEditModel) => {
    return !pkg.packageAddon || pkg.packageAddon.type === PackageAddonTypes.STANDARD;
  };

  /**
   * Confirms the package is of the specified type
   * @param pkg
   * @param type
   * @returns
   */
  const isAddonType = <T>(
    pkg: BasePackageDisplayModel<T> | Cartv2PackageDisplayModel,
    type: `${PackageAddonTypes}`,
  ) => {
    if (type === PackageAddonTypes.STANDARD) return isStandardPackage(pkg);
    else return pkg.packageAddon && pkg.packageAddon.type === type;
  };

  /**
   * Is addon package valid
   * @param pkg Package to validate
   * @param pkgRetailCost Package retail cost
   * @param total Cart total
   * @param packages Cart packages
   */
  const validatePackage = <T, I>(
    pkg: BasePackageDisplayModel<T> | Cartv2PackageDisplayModel,
    pkgRetailCost: number,
    total: number,
    items?: PackageItems<I>,
  ) => {
    if (isStandardPackage(pkg)) return true;
    if (items && !hasStandardPackage(items)) return false;
    if (isAddonType(pkg, PackageAddonTypes.ADDON_PRICE)) {
      return subtract(total, pkgRetailCost) >= (pkg.packageAddon!.requiredMinPrice ?? 0);
    }
    return true;
  };

  /**
   * Check if cart has a standard package
   * @param packages
   * @returns
   */
  const hasStandardPackage = <T>(packages: PackageItems<T>): boolean => {
    if (packages === undefined) return false;

    return packages.some((p) => isStandardPackage(p));
  };

  /**
   * Confirm package is an addon
   * @param pkg
   * @returns
   */
  const isAddon = <T>(pkg: BasePackageDisplayModel<T> | Cartv2PackageDisplayModel) => {
    return !isStandardPackage(pkg);
  };

  /**
   * Get the type of package
   * @param pkg
   * @returns
   */
  const getType = <T>(pkg: BasePackageDisplayModel<T> | Cartv2PackageDisplayModel) => {
    return isStandardPackage(pkg) ? PackageAddonTypes.STANDARD : pkg.packageAddon!.type;
  };

  const isAvailable = <T>(pkg: BasePackageDisplayModel<T>, hasStandardPackage: boolean, total: number) => {
    if (isAddonType(pkg, PackageAddonTypes.ADDON_STANDARD)) {
      return hasStandardPackage;
    } else {
      return pkg.packageAddon!.requiredMinPrice! <= total;
    }
  };

  return {
    isStandardPackage,
    isAddonType,
    validatePackage,
    getType,
    hasStandardPackage,
    isAddon,
    isAvailable,
  };
};
